import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
    BarChart3, ChevronRight, X, Loader2,
    Banknote, ReceiptText, ShoppingBag, Target, Timer, Truck, Gauge, UtensilsCrossed,
    TrendingUp, TrendingDown, Equal,
} from 'lucide-react';
import { OK, WARN, BAD, TEAL, KANAL_BARVA, MODAL_SKLO, MODAL_POZADI } from '../../lib/uiTokens';
import useRozmer from '../../hooks/useRozmer';
import SheetModal, { SheetKarta } from '../../components/SheetModal';

const ORANZ = '#FF8A00';

// Ikona + akcent metriky. Akcent = kariéra, ze které metrika pochází
// (obsluha tyrkys, rozvoz oranž) — stejnou barvou jsou kariéry v hlavičce.
const METRIKY = {
    trzba:      { Icon: Banknote,    akcent: TEAL },
    prumer:     { Icon: ReceiptText, akcent: TEAL },
    objednavky: { Icon: ShoppingBag, akcent: TEAL },
    slib:       { Icon: Target,      akcent: ORANZ },
    min60:      { Icon: Timer,       akcent: ORANZ },
    pocet:      { Icon: Truck,       akcent: ORANZ },
    ridic:      { Icon: Gauge,       akcent: ORANZ },
};
// Krátké popisky pro malé dlaždice — plný popisek zůstává v tooltipu a v detailu.
const KRATKE_POPISKY = {
    trzba: 'Tržba', prumer: 'Průměr', objednavky: 'Objednávky',
    slib: 'Slib', min60: 'Do 60 min', pocet: 'Rozvozy', ridic: 'Řidič',
};
const KARIERA_BARVA = { obsluha: TEAL, rozvoz: ORANZ };
const KARIERA_IKONA = { obsluha: UtensilsCrossed, rozvoz: Truck };
const ZNAMKA_BARVA = { good: OK, warn: WARN, bad: BAD };

const MEZERA = 10;
const MIN_SIRKA = 210;  // užší dlaždice by uřízla popisek — radši další řádek
const NOUZOVA_SIRKA = 140; // …pokud by řádky vyšly moc nízké, smí se jít až sem
const MAX_VYSKA = 300;  // vyšší řádek je jen prázdné místo — radši další řádek

const sevri = (min, v, max) => Math.max(min, Math.min(max, v));
const fmtCislo = (v, desetiny = 0) => Number(v).toLocaleString('cs-CZ', { maximumFractionDigits: desetiny });

/** Hodnota metriky rozdělená na číslo a jednotku (jednotka se kreslí menší). */
function rozloz(hodnota, jednotka) {
    if (hodnota == null) return { cislo: '—', jednotka: '' };
    return { cislo: fmtCislo(hodnota, jednotka === '%' ? 1 : 0), jednotka: jednotka || '' };
}

/**
 * Srovnání s mediánem týmu počítané na klientu z čísel — server posílá medián
 * i s desetinami („84 891,5 Kč") a nulový rozdíl jako „+0".
 */
function srovnaniSTymem(m) {
    if (!m.srovnani || m.tym == null || m.hodnota == null) return null;
    const rozdil = m.hodnota - m.tym;
    const procenta = m.jednotka === '%';
    const tym = `${rozloz(m.tym, m.jednotka).cislo}${m.jednotka ? ` ${m.jednotka}` : ''}`;
    if (Math.abs(rozdil) < (procenta ? 0.5 : 1)) return { smer: 'stejne', text: 'jako tým', tym };
    const suffix = procenta ? ' p.b.' : (m.jednotka === 'Kč' ? ' Kč' : '');
    return {
        smer: rozdil > 0 ? 'nahoru' : 'dolu',
        text: `${rozdil > 0 ? '+' : '−'}${fmtCislo(Math.abs(rozdil), procenta ? 1 : 0)}${suffix}`,
        tym,
    };
}

/**
 * Rozvrh mřížky: kolik dlaždic v kterém řádku. Sloupců tolik, kolik unese
 * šířka; řádků tolik, aby žádný nebyl přehnaně vysoký. Dlaždice se rozdělí
 * rovnoměrně (7 → 3 + 2 + 2), každý řádek vyplní celou šířku.
 */
function rozvrhni(n, sirka, vyska) {
    if (!n) return [];
    const radkuPro = (sloupcu) => Math.min(n, Math.max(Math.ceil(n / sloupcu), Math.ceil(vyska / MAX_VYSKA)));
    const vyskaRadku = (radku) => (vyska - MEZERA * (radku - 1)) / radku;

    let sloupcu = sevri(1, Math.floor((sirka + MEZERA) / (MIN_SIRKA + MEZERA)), 4);
    // Na nízké a úzké ploše radši užší dlaždice než řádky, do kterých se nic nevejde.
    while (sloupcu < 4 && vyskaRadku(radkuPro(sloupcu)) < 70
        && (sirka - MEZERA * sloupcu) / (sloupcu + 1) >= NOUZOVA_SIRKA) {
        sloupcu++;
    }
    const radku = radkuPro(sloupcu);
    const zaklad = Math.floor(n / radku);
    const navic = n % radku;
    return Array.from({ length: radku }, (_, i) => zaklad + (i < navic ? 1 : 0));
}

/**
 * Moje KPI — pravá třetina horní řady cockpitu. Jednoduchá mřížka stejných
 * dlaždic, která vždy vyplní plochu bez scrollu; obsah dlaždice se přizpůsobí
 * její velikosti.
 *
 * Stejná data i sada metrik jako KPI obrazovka zaměstnanecké appky
 * (FofrLamaApp/src/screens/Kpi.jsx) nad `/api/me/kpi/prehled`. Server skládá
 * karty podle kariér zaměstnance (EmployeeKpiService::vykon):
 *   - Obsluha → tržba na mých směnách, průměr / objednávku, počet objednávek
 *     s rozpadem kanálů; čistá obsluha navíc provozní dovoz (slib, do 60 min)
 *   - Rozvoz  → dovoz vůči slibu, do 60 min, počet rozvozů, ujeto / známka řidiče
 *   - dual    → obojí, dovozová KPI jen jednou (osobní z rozvozu)
 *
 * Bez vlastní hlavičky a přepínače období: čísla jsou vždy za měsíc, který je
 * otevřený v kalendáři pod nimi (`obdobi` = 'YYYY-MM', drží ho Přehled).
 * Listováním kalendáře se KPI přepočítají i za minulé měsíce.
 *
 * Souhrn živé / poslední směny z appky tu schválně není — na směně ho lidé
 * mají v telefonu. Klik na dlaždici → detail s grafem.
 */
// `zaklad` — odkud se KPI berou: vlastní (`/api/me/kpi`), nebo člověka z týmu
// (`/api/me/tym/{id}/kpi`, Cockpit → Můj tým). Odpověď má v obou případech stejný tvar.
export default function MojeKpi({ isDark, cardStyle, kariery = [], obdobi = 'mesic', mobil = false, zaklad = '/api/me/kpi' }) {
    const [data, setData] = useState(null);
    const [stav, setStav] = useState('loading'); // loading | ok | error
    const [obnovuji, setObnovuji] = useState(false);
    const [detail, setDetail] = useState(null); // { klic, label }
    const [plochaRef, plocha] = useRozmer();

    const nacti = useCallback(() => {
        setStav(s => (s === 'ok' ? 'ok' : 'loading'));
        setObnovuji(true);
        return fetch(`${zaklad}/prehled?obdobi=${obdobi}`)
            .then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
            .then(d => { setData(d); setStav('ok'); })
            .catch(() => setStav(s => (s === 'ok' ? 'ok' : 'error')))
            .finally(() => setObnovuji(false));
    }, [obdobi, zaklad]);

    useEffect(() => { nacti(); }, [nacti]);

    const barvy = useMemo(() => ({
        isDark,
        textPrimary: isDark ? '#fff' : 'var(--text-primary)',
        textMuted: isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)',
        labelMuted: isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)',
        hranice: isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)',
        hraniceHover: isDark ? 'rgba(255,255,255,0.16)' : 'var(--border-strong)',
        stopa: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)',
    }), [isDark]);

    const serazeneKariery = [...(kariery || [])].sort((a, b) => (a.poradi ?? 0) - (b.poradi ?? 0));

    // Kariéra, jejíž karty jsou všechny bez dat (celý měsíc žádný rozvoz / žádná
    // směna na obsluze), se sbalí do jednoho úzkého řádku pod dlaždicemi —
    // čtyři velké prázdné dlaždice by přebily ty se skutečnými čísly.
    const { karty, prazdneKariery } = useMemo(() => {
        const poKariere = new Map();
        for (const m of data?.vykon || []) {
            const k = m.kariera || '';
            if (!poKariere.has(k)) poKariere.set(k, []);
            poKariere.get(k).push(m);
        }
        const datove = [];
        const prazdne = [];
        for (const [kariera, seznam] of poKariere) {
            if (kariera && seznam.every(m => m.bez_dat)) {
                prazdne.push({ kariera, text: seznam.find(m => m.individual_text)?.individual_text });
            } else {
                datove.push(...seznam);
            }
        }
        return { karty: datove, prazdneKariery: prazdne };
    }, [data]);

    const rozvrh = rozvrhni(karty.length, plocha.w || 600, plocha.h || 400);
    let dalsi = 0;
    const radky = rozvrh.map(pocet => karty.slice(dalsi, (dalsi += pocet)));

    return (
        <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', gap: MEZERA }}>
            {/* Plocha dlaždic — nikdy nescrolluje, mřížka se vejde vždy */}
            <div ref={plochaRef} style={{
                flex: 1, minHeight: 0, overflow: 'hidden',
                display: 'flex', flexDirection: 'column', gap: MEZERA,
                opacity: obnovuji && stav === 'ok' ? 0.45 : 1, transition: 'opacity 0.15s ease',
            }}>
                {stav === 'loading' && (
                    <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: MEZERA }}>
                        {[0, 1, 2].map(i => (
                            <div key={i} className="rounded-xl animate-pulse" style={{ ...cardStyle, flex: 1 }} />
                        ))}
                    </div>
                )}

                {stav === 'error' && (
                    <Hlaska cardStyle={cardStyle} barvy={barvy} text="KPI se nepodařilo načíst.">
                        <button type="button" onClick={nacti} style={{
                            fontSize: 11, fontWeight: 600, padding: '5px 12px', borderRadius: 7, cursor: 'pointer',
                            background: 'none', border: `1px solid ${barvy.hranice}`, color: barvy.labelMuted,
                        }}>Zkusit znovu</button>
                    </Hlaska>
                )}

                {stav === 'ok' && karty.length === 0 && (
                    <Hlaska cardStyle={cardStyle} barvy={barvy} text={serazeneKariery.length || prazdneKariery.length
                        ? 'V tomto měsíci nejsou výkonová data — objeví se po první odpracované směně.'
                        : 'KPI se odvíjí od kariéry. Jakmile ti ji manažer přiřadí, uvidíš tu své ukazatele.'} />
                )}

                {stav === 'ok' && radky.map((radek, ri) => (
                    <div key={ri} style={{ flex: '1 1 0', minHeight: 0, display: 'flex', gap: MEZERA }}>
                        {radek.map(m => (
                            <Dlazdice key={m.klic} metrika={m} cardStyle={cardStyle} barvy={barvy}
                                onOpen={() => setDetail({ klic: m.klic, label: m.label })} />
                        ))}
                    </div>
                ))}
            </div>

            {/* Kariéry bez dat — úzké řádky pod mřížkou (mimo měřenou plochu,
                takže si dlaždice výšku přepočítají samy). */}
            {stav === 'ok' && prazdneKariery.map(({ kariera, text }) => {
                const Ikona = KARIERA_IKONA[kariera] || BarChart3;
                const nazev = serazeneKariery.find(k => (k.kod || '').toLowerCase() === kariera)?.nazev
                    || kariera.charAt(0).toUpperCase() + kariera.slice(1);
                return (
                    <div key={kariera} className="rounded-xl" style={{
                        ...cardStyle, flexShrink: 0, minWidth: 0, height: 40,
                        display: 'flex', alignItems: 'center', gap: 8, padding: '0 14px',
                        opacity: obnovuji ? 0.45 : 1, transition: 'opacity 0.15s ease',
                    }}>
                        <Ikona size={14} style={{ color: KARIERA_BARVA[kariera] || barvy.labelMuted, flexShrink: 0 }} />
                        <span style={{ fontSize: 12, fontWeight: 600, color: barvy.labelMuted, whiteSpace: 'nowrap' }}>{nazev}</span>
                        <span style={{
                            fontSize: 11.5, color: barvy.textMuted, minWidth: 0,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>· {text || 'v tomto období bez dat'}</span>
                    </div>
                );
            })}

            {detail && (
                <KpiDetailModal klic={detail.klic} label={detail.label} obdobi={obdobi} zaklad={zaklad}
                    barvy={barvy} mobil={mobil} onClose={() => setDetail(null)} />
            )}
        </div>
    );
}

function Hlaska({ cardStyle, barvy, text, children }) {
    return (
        <div className="rounded-xl" style={{
            ...cardStyle, flex: 1, minHeight: 0, overflow: 'hidden',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 10, padding: 16, textAlign: 'center',
        }}>
            <span style={{ fontSize: 12.5, color: barvy.labelMuted, maxWidth: 320, lineHeight: 1.5 }}>{text}</span>
            {children}
        </div>
    );
}

/** Proužek rozpadu objednávek po kanálech. */
function ProuzekKanalu({ rozpad = [], vyska = 6, barvy }) {
    const celkem = rozpad.reduce((s, r) => s + r.n, 0);
    return (
        <div title={rozpad.map(r => `${r.k} ${r.n}`).join(' · ')} style={{
            display: 'flex', gap: 2, height: vyska, borderRadius: vyska, overflow: 'hidden', background: barvy.stopa,
        }}>
            {celkem > 0 && rozpad.map(r => r.n > 0 && (
                <i key={r.kanal} style={{
                    width: `${(r.n / celkem) * 100}%`, height: '100%',
                    background: KANAL_BARVA[r.kanal] || barvy.textMuted,
                }} />
            ))}
        </div>
    );
}

function Znamka({ m, velikost, barvy }) {
    const b = ZNAMKA_BARVA[m.znamka_stav];
    return (
        <span title="Známka řidiče (A = nejlepší)" style={{
            width: velikost, height: velikost, borderRadius: Math.round(velikost * 0.28), flexShrink: 0,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontSize: Math.round(velikost * 0.52), fontWeight: 800, lineHeight: 1,
            color: b || barvy.textMuted,
            background: b ? `${b}24` : barvy.stopa,
            border: `1px solid ${b ? `${b}55` : barvy.hranice}`,
        }}>{m.znamka}</span>
    );
}

function Rozdil({ sr, font = 11, barvy }) {
    if (!sr) return null;
    const barva = sr.smer === 'nahoru' ? OK : sr.smer === 'dolu' ? WARN : barvy.labelMuted;
    const Ikona = sr.smer === 'nahoru' ? TrendingUp : sr.smer === 'dolu' ? TrendingDown : Equal;
    return (
        <b style={{
            display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0, whiteSpace: 'nowrap',
            fontSize: font, fontWeight: 700, color: barva, fontVariantNumeric: 'tabular-nums',
        }}>
            <Ikona size={Math.round(font + 1)} strokeWidth={2.6} /> {sr.text}
        </b>
    );
}

/**
 * Jedna KPI dlaždice. Podle vlastní výšky má tři podoby:
 *   plná    — ikona + popisek / velké číslo / řádek srovnání s týmem
 *   nízká   — popisek + rozdíl / číslo
 *   řádek   — popisek … číslo … rozdíl (vše na jedné lince)
 * Velikost čísla se odvodí z výšky i šířky, takže se nikdy neořízne.
 */
function Dlazdice({ metrika: m, cardStyle, barvy, onOpen }) {
    const [ref, rozmer] = useRozmer();
    const [hover, setHover] = useState(false);
    const { Icon, akcent } = METRIKY[m.klic] || { Icon: BarChart3, akcent: ORANZ };

    const typ = m.typ === 'pocet_rozpad' ? 'rozpad' : m.typ === 'ridic' ? 'ridic' : 'srovnani';
    const hodnota = typ === 'rozpad' ? { cislo: m.hodnota_label ?? '—', jednotka: '' }
        : typ === 'ridic' ? { cislo: String(m.hodnota_label || '').replace(/\s*km$/, '') || '—', jednotka: 'km' }
            : rozloz(m.hodnota, m.jednotka);
    const sr = typ === 'srovnani' ? srovnaniSTymem(m) : null;

    const w = rozmer.w || 240;
    const h = rozmer.h || 160;
    // Spodní řádek plné podoby: proužek + legenda u rozpadu je o kus vyšší.
    const spodek = typ === 'rozpad' ? 30 : 18;
    const podoba = h >= 100 + spodek + (typ === 'ridic' ? 6 : 0) ? 'plna' : h >= 64 ? 'nizka' : 'radek';

    // Šířka čísla ≈ 0,62 em na znak (+ menší jednotka) → strop písma z šířky.
    const faktor = 0.62 * String(hodnota.cislo).length + (hodnota.jednotka ? 0.32 * hodnota.jednotka.length + 0.2 : 0);
    const strop = (dostupno) => Math.max(11, dostupno / faktor);

    // Výškové rozpočty (vnější rozměr): plná = odsazení 32 + hlavička 26 + mezera 8
    // + číslo + 10 + spodek; nízká = 22 + 18 + 4 + číslo.
    let font;
    if (podoba === 'plna') {
        // Známka řidiče stojí vedle čísla a je o 6 px vyšší než ono.
        const ridic = typ === 'ridic';
        font = Math.min(52, h * 0.22, h - 76 - spodek - (ridic ? 6 : 0), strop(w - 32 - (ridic ? 58 : 0)));
    } else if (podoba === 'nizka') {
        // Rozdíl s týmem je v řádku popisku, číslo má celou šířku (u rozpadu jen proužek vedle).
        font = Math.min(28, h - 46, strop(w - 24 - (typ === 'rozpad' ? 60 : 0)));
    } else {
        font = Math.min(20, h - 16, strop((w - 24) * 0.45));
    }
    font = Math.round(Math.max(11, font));

    const cislo = (
        <span style={{
            fontSize: font, fontWeight: 800, lineHeight: 1, letterSpacing: font > 24 ? -0.5 : 0,
            color: m.bez_dat ? barvy.textMuted : barvy.textPrimary,
            fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', flexShrink: 0,
        }}>
            {hodnota.cislo}
            {hodnota.jednotka && (
                <small style={{ fontSize: Math.round(font * 0.5), fontWeight: 600, color: barvy.labelMuted, marginLeft: 3, letterSpacing: 0 }}>
                    {hodnota.jednotka}
                </small>
            )}
        </span>
    );
    const popisek = (velikost, kratky = false) => (
        <span title={m.label} style={{
            flex: 1, minWidth: 0, fontSize: velikost, fontWeight: 600, color: barvy.labelMuted,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{kratky ? (KRATKE_POPISKY[m.klic] || m.label) : m.label}</span>
    );

    return (
        <button ref={ref} type="button" onClick={onOpen}
            onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
            className="rounded-xl"
            style={{
                ...cardStyle,
                flex: '1 1 0', minWidth: 0, minHeight: 0, overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
                padding: podoba === 'plna' ? 15 : podoba === 'nizka' ? '10px 12px' : '0 12px',
                textAlign: 'left', cursor: 'pointer', color: 'inherit', font: 'inherit',
                border: `1px solid ${hover ? barvy.hraniceHover : barvy.hranice}`,
                transition: 'border-color 0.15s ease',
            }}>
            {podoba === 'plna' && (
                <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0, height: 26 }}>
                        <span style={{
                            width: 26, height: 26, borderRadius: 8, flexShrink: 0,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            background: `${akcent}1f`, color: akcent,
                        }}><Icon size={14} /></span>
                        {popisek(12)}
                        <ChevronRight size={15} style={{ flexShrink: 0, color: barvy.textMuted, opacity: hover ? 1 : 0, transition: 'opacity 0.15s ease' }} />
                    </div>

                    <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                        {cislo}
                        {typ === 'ridic' && <Znamka m={m} velikost={font + 6} barvy={barvy} />}
                    </div>

                    <div style={{ marginTop: 10, height: spodek, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 6, minWidth: 0 }}>
                        {typ === 'srovnani' && (
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, fontSize: 11, color: barvy.textMuted }}>
                                {sr ? <Rozdil sr={sr} barvy={barvy} /> : null}
                                <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {sr ? `tým ${sr.tym}` : (m.individual_text || 'bez srovnání s týmem')}
                                </span>
                            </span>
                        )}
                        {typ === 'rozpad' && (
                            <>
                                <ProuzekKanalu rozpad={m.rozpad} vyska={7} barvy={barvy} />
                                <span style={{ display: 'flex', gap: 12, fontSize: 11, color: barvy.textMuted, whiteSpace: 'nowrap', overflow: 'hidden' }}>
                                    {(m.rozpad || []).map(r => (
                                        <span key={r.kanal} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontVariantNumeric: 'tabular-nums' }}>
                                            <i style={{ width: 7, height: 7, borderRadius: 2, background: KANAL_BARVA[r.kanal] || barvy.textMuted }} />
                                            {w >= 250 && `${r.k} `}<b style={{ color: barvy.textPrimary }}>{r.n}</b>
                                        </span>
                                    ))}
                                </span>
                            </>
                        )}
                        {typ === 'ridic' && (
                            <span style={{ fontSize: 11, color: barvy.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {m.individual_text || 'ujeto · známka z jízdního stylu'}
                            </span>
                        )}
                    </div>
                </>
            )}

            {podoba === 'nizka' && (
                <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0, height: 18 }}>
                        <Icon size={13} style={{ color: akcent, flexShrink: 0 }} />
                        {popisek(11.5, true)}
                        {typ === 'ridic' && <Znamka m={m} velikost={18} barvy={barvy} />}
                        {sr && <span title={`tým ${sr.tym}`} style={{ flexShrink: 0 }}><Rozdil sr={sr} font={10.5} barvy={barvy} /></span>}
                    </div>
                    <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                        {cislo}
                        {typ === 'rozpad' && <div style={{ flex: 1, minWidth: 30 }}><ProuzekKanalu rozpad={m.rozpad} vyska={6} barvy={barvy} /></div>}
                    </div>
                </>
            )}

            {podoba === 'radek' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: '100%', minWidth: 0 }}>
                    <Icon size={13} style={{ color: akcent, flexShrink: 0 }} />
                    {popisek(11.5, true)}
                    {cislo}
                    {typ === 'ridic' && <Znamka m={m} velikost={Math.min(20, font + 4)} barvy={barvy} />}
                    {sr && w >= 260 && <span title={`tým ${sr.tym}`}><Rozdil sr={sr} font={10.5} barvy={barvy} /></span>}
                </div>
            )}
        </button>
    );
}

/** Malý SVG graf: ty (oranžová plná) vs medián týmu (šedá čárkovaná). Port z appky. */
function GrafTyVsTym({ ty = [], median = [] }) {
    if (!ty.length) return null;
    const w = 400, h = 150, pad = 8;
    const vsechny = [...ty, ...(median || [])].filter(v => v != null);
    const max = Math.max(...vsechny) * 1.12 || 1;
    const min = Math.min(...vsechny) * 0.9;
    const rozsah = max - min || 1;
    const x = i => pad + i * ((w - 2 * pad) / Math.max(ty.length - 1, 1));
    const y = v => h - pad - ((v - min) / rozsah) * (h - 2 * pad);
    const cesta = arr => arr.map((v, i) => (i ? 'L' : 'M') + x(i).toFixed(1) + ' ' + y(v).toFixed(1)).join(' ');

    return (
        <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ overflow: 'visible', display: 'block' }}>
            {median?.length > 0 && (
                <path d={cesta(median)} fill="none" stroke="#8b8b9a" strokeWidth="2" strokeDasharray="4 4" vectorEffect="non-scaling-stroke" />
            )}
            <path d={cesta(ty)} fill="none" stroke={ORANZ} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
            {ty.map((v, i) => (
                <circle key={i} cx={x(i).toFixed(1)} cy={y(v).toFixed(1)} r="3" fill={ORANZ} />
            ))}
        </svg>
    );
}

/**
 * Detail KPI — velká hodnota, graf ty vs medián, insight a rozpad.
 * Desktop: modál uprostřed. Mobil: standardní sheet (components/SheetModal).
 */
function KpiDetailModal({ klic, label, obdobi, barvy, onClose, mobil = false, zaklad = '/api/me/kpi' }) {
    const [data, setData] = useState(null);
    const [stav, setStav] = useState('loading');
    const { Icon, akcent } = METRIKY[klic] || { Icon: BarChart3, akcent: ORANZ };

    useEffect(() => {
        let ziv = true;
        setStav('loading');
        fetch(`${zaklad}/detail/${encodeURIComponent(klic)}?obdobi=${obdobi}`)
            .then(r => (r.ok ? r.json() : Promise.reject(new Error(r.status))))
            .then(d => { if (ziv) { setData(d); setStav('ok'); } })
            .catch(() => { if (ziv) setStav('error'); });
        return () => { ziv = false; };
    }, [klic, obdobi, zaklad]);

    useEffect(() => {
        if (mobil) return undefined; // sheet má Esc sám
        const onKey = e => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose, mobil]);

    const maGraf = Array.isArray(data?.ty) && data.ty.length > 0;
    const slot = barvy.isDark ? 'rgba(255,255,255,0.03)' : 'var(--surface-elevated)';

    if (mobil) {
        return (
            <SheetModal onClose={onClose} isDark={barvy.isDark}
                ikona={Icon} barva={akcent} nadpis={label}
                podnadpis={data?.podtitulek || null}>
                {stav === 'loading' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: barvy.textMuted, padding: '24px 2px' }}>
                        <Loader2 size={15} className="animate-spin" /> Načítám detail…
                    </div>
                )}
                {stav === 'error' && (
                    <div style={{ fontSize: 13, color: BAD, padding: '12px 2px' }}>Detail se nepodařilo načíst.</div>
                )}
                {stav === 'ok' && data && (
                    <>
                        <SheetKarta isDark={barvy.isDark} style={{ padding: '14px 14px 12px' }}>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                                <span style={{
                                    fontSize: 34, fontWeight: 800, lineHeight: 1, letterSpacing: -0.5,
                                    color: barvy.textPrimary, fontVariantNumeric: 'tabular-nums',
                                }}>{data.velka_hodnota}</span>
                                {data.delta && (
                                    <span style={{ fontSize: 13, fontWeight: 600, color: barvy.labelMuted, fontVariantNumeric: 'tabular-nums' }}>
                                        {data.delta}
                                    </span>
                                )}
                            </div>
                            {data.insight && (
                                <div style={{ fontSize: 12.5, lineHeight: 1.5, color: barvy.labelMuted, marginTop: 10 }}>{data.insight}</div>
                            )}
                        </SheetKarta>

                        {maGraf && (
                            <SheetKarta isDark={barvy.isDark} style={{ padding: 12 }}>
                                <div style={{ display: 'flex', gap: 14, fontSize: 11, color: barvy.textMuted, marginBottom: 8 }}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                        <i style={{ width: 10, height: 3, borderRadius: 2, background: ORANZ }} />Ty
                                    </span>
                                    {data.median_tym?.length > 0 && (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                            <i style={{ width: 10, height: 3, borderRadius: 2, background: '#8b8b9a' }} />Medián týmu
                                        </span>
                                    )}
                                </div>
                                <GrafTyVsTym ty={data.ty} median={data.median_tym} />
                            </SheetKarta>
                        )}

                        {Array.isArray(data.radky) && data.radky.length > 0 && (
                            <SheetKarta isDark={barvy.isDark} style={{ padding: 0, overflow: 'hidden' }}>
                                {data.radky.map((r, i) => (
                                    <div key={i} style={{
                                        display: 'flex', justifyContent: 'space-between', gap: 12,
                                        padding: '11px 14px', fontSize: 13,
                                        borderTop: i ? `1px solid ${barvy.hranice}` : 'none',
                                    }}>
                                        <span style={{ color: barvy.labelMuted }}>{r[0]}</span>
                                        <span style={{ color: barvy.textPrimary, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{r[1]}</span>
                                    </div>
                                ))}
                            </SheetKarta>
                        )}
                    </>
                )}
            </SheetModal>
        );
    }

    return createPortal((
        <div onClick={onClose} style={{
            position: 'fixed', inset: 0, zIndex: 99990, ...MODAL_POZADI,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}>
            <div onClick={e => e.stopPropagation()} style={{
                // Rám drží výšku a NEscrolluje — scrolluje až tělo pod hlavičkou.
                // Dokud byl `overflowY: auto` přímo tady, na sloupcovém flexu se
                // obsah nescroloval, ale **smrštil**: přímé děti mají
                // `flex-shrink: 1`, takže se při přetečení zmáčknou a
                // `scrollHeight` se rovná `clientHeight` — modal vypadá useknutě
                // a nejde s ním hnout. Přesně tohle hlásili řidiči u dlaždice
                // s kilometry. `SheetModal` to má rozdělené od začátku.
                // `dvh`, ne `vh`: na telefonu počítá `vh` s výřezem bez lišty
                // prohlížeče a modal přeteče pod spodní hranu displeje.
                width: '100%', maxWidth: 480, maxHeight: '88dvh',
                ...MODAL_SKLO,
                padding: 20, display: 'flex', flexDirection: 'column', gap: 14,
                overflow: 'hidden',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                    <span style={{
                        width: 34, height: 34, borderRadius: 6, flexShrink: 0,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        background: `${akcent}1f`, color: akcent,
                    }}><Icon size={17} /></span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: barvy.textPrimary }}>{label}</div>
                        {data?.podtitulek && (
                            <div style={{ fontSize: 11.5, color: barvy.textMuted, marginTop: 2 }}>{data.podtitulek}</div>
                        )}
                    </div>
                    <button type="button" onClick={onClose} aria-label="Zavřít" style={{
                        background: 'none', border: 'none', cursor: 'pointer', color: barvy.textMuted, padding: 2,
                    }}><X size={18} /></button>
                </div>

                {/* Tělo: jediné místo, které scrolluje. `minHeight: 0` je nutné —
                    bez něj má flexová položka minimum podle obsahu a nezmenší se,
                    takže by scroll opět neměl kde vzniknout. */}
                <div style={{
                    flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain',
                    display: 'flex', flexDirection: 'column', gap: 14,
                }}>
                {stav === 'loading' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: barvy.textMuted, padding: '24px 0', flexShrink: 0 }}>
                        <Loader2 size={14} className="animate-spin" /> Načítám detail…
                    </div>
                )}
                {stav === 'error' && (
                    <div style={{ fontSize: 12, color: BAD, padding: '12px 0', flexShrink: 0 }}>Detail se nepodařilo načíst.</div>
                )}

                {stav === 'ok' && data && (
                    <>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', flexShrink: 0 }}>
                            <span style={{
                                fontSize: 34, fontWeight: 800, lineHeight: 1, letterSpacing: -0.5,
                                color: barvy.textPrimary, fontVariantNumeric: 'tabular-nums',
                            }}>{data.velka_hodnota}</span>
                            {data.delta && (
                                <span style={{ fontSize: 13, fontWeight: 600, color: barvy.labelMuted, fontVariantNumeric: 'tabular-nums' }}>
                                    {data.delta}
                                </span>
                            )}
                        </div>

                        {maGraf && (
                            <div style={{ borderRadius: 6, padding: 12, background: slot, border: `1px solid ${barvy.hranice}`, flexShrink: 0 }}>
                                <div style={{ display: 'flex', gap: 14, fontSize: 11, color: barvy.textMuted, marginBottom: 8 }}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                        <i style={{ width: 10, height: 3, borderRadius: 2, background: ORANZ }} />Ty
                                    </span>
                                    {data.median_tym?.length > 0 && (
                                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                            <i style={{ width: 10, height: 3, borderRadius: 2, background: '#8b8b9a' }} />Medián týmu
                                        </span>
                                    )}
                                </div>
                                <GrafTyVsTym ty={data.ty} median={data.median_tym} />
                            </div>
                        )}

                        {data.insight && (
                            <div style={{ fontSize: 12, lineHeight: 1.55, color: barvy.labelMuted, flexShrink: 0 }}>{data.insight}</div>
                        )}

                        {Array.isArray(data.radky) && data.radky.length > 0 && (
                            <div style={{ borderRadius: 6, border: `1px solid ${barvy.hranice}`, overflow: 'hidden', flexShrink: 0 }}>
                                {data.radky.map((r, i) => (
                                    <div key={i} style={{
                                        display: 'flex', justifyContent: 'space-between', gap: 12,
                                        padding: '8px 12px', fontSize: 12,
                                        borderTop: i ? `1px solid ${barvy.hranice}` : 'none',
                                    }}>
                                        <span style={{ color: barvy.labelMuted }}>{r[0]}</span>
                                        <span style={{ color: barvy.textPrimary, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{r[1]}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
                </div>
            </div>
        </div>
    ), document.body);
}
